import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Table, TableBody, TableCell, TableHead, TableRow, Typography,
  Button, LinearProgress, TablePagination, Alert, } from '@mui/material';
import { EmojiEvents, Grading, PictureAsPdfOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import {
  admissionCycleService, MeritListResponse,
} from '../../services/admissions/admissionCycleService';
import {
  AdmissionCycle, AdmissionApplication,
} from '../../types/admissions';
import StatusChip, { ToneChip } from '../common/StatusChip';
import { assessmentPlan, AssessmentPlan, pathwayFor, roundsLabel, testOutcome, pendingOverride } from '../../utils/admissionAssessment';
import EmptyState from '../common/EmptyState';
import TableRowSkeletons from '../common/TableRowSkeletons';
import CandidateDrawer from './CandidateDrawer';
import ApplicationActionsMenu from './ApplicationActionsMenu';
import SeatSnapshotStrip from './SeatSnapshotStrip';
import {
  TabToolbar, TableCard, RankBadge, tableHeadCellSx, clickableRowSx,
  ClassSearchFilters, ApplicantCell, RowActionsCell, SeatCutLineRow, NoSearchMatchRow,
  applicantMatches, useCycleClassOptions, numCellSx,
} from './tabLayout';
import { useDebounce } from '../../hooks/useDebounce';
import { usePdfExport, useSchoolBranding } from '../../utils/pdf';
import { fmtNumber } from '../../utils/pdf/format';
import { MeritListDocument } from './pdf';
import PdfRenderingDialog from '../common/PdfRenderingDialog';
import BusyIcon from '../common/BusyIcon';

/**
 * MeritListTab — the ranked list for one class, with the seat snapshot on
 * top. Ranks refresh by themselves whenever marks or statuses change, using
 * the cycle's merit formula. Applicants still missing marks are listed
 * after the ranked ones with what's missing. A dashed cut-line separates the
 * ranks inside the seat cap from those outside.
 */

const PAGE_SIZE_OPTIONS = [25, 50, 100];

interface Props {
  cycle: AdmissionCycle;
  /** Opens the marks spreadsheet upload; omitted when the user can't enter marks. */
  onUploadMarks?: () => void;
}

const MeritListTab: React.FC<Props> = ({ cycle, onUploadMarks }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [classId, setClassId] = useState<string>('');
  const [data, setData] = useState<MeritListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(50);
  const [openId, setOpenId] = useState<string | null>(null);
  // Row ⋮ menu — one shared menu, anchored to whichever row opened it.
  const [rowMenu, setRowMenu] = useState<{ anchor: HTMLElement; app: AdmissionApplication } | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 250);

  // Client-side filter — merit backend doesn't accept a search param, so
  // this narrows within the current page. For broader searches raise the
  // page size.
  const visibleItems = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.items;
    return data.items.filter(app => applicantMatches(app, q));
  }, [data, search]);

  const classOptions = useCycleClassOptions(cycle);
  // The class's own pathway decides the columns and the pass mark.
  const plan = useMemo(() => assessmentPlan(pathwayFor(cycle, classId)), [cycle, classId]);

  // Preselect first class as soon as options are available.
  useEffect(() => {
    if (!classId && classOptions.length > 0) {
      setClassId(classOptions[0]._id);
    }
  }, [classId, classOptions]);

  const load = useCallback(async () => {
    if (!classId) return;
    setLoading(true);
    try {
      const res = await admissionCycleService.meritList(cycle._id, {
        classId,
        page: page + 1,
        limit,
      });
      setData(res);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to load merit list', { variant: 'error' });
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [cycle._id, classId, page, limit, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [classId]);

  // ── PDF export ────────────────────────────────────────────────────────────
  const { branding } = useSchoolBranding();
  const { generate: generatePdf, loading: pdfLoading } = usePdfExport();
  const [exporting, setExporting] = useState(false);
  const className = useMemo(
    () => classOptions.find(c => c._id === classId)?.name,
    [classOptions, classId],
  );

  const downloadPdf = async () => {
    if (!data) return;
    setExporting(true);
    try {
      // Pull every ranked row for the class so the PDF is the full list,
      // not just the visible page. The merit backend returns `pagination`,
      // so we page through if there are more than one page's worth.
      const allItems: AdmissionApplication[] = [...data.items];
      const totalPages = data.pagination?.totalPages ?? 1;
      if (totalPages > 1) {
        for (let p = 2; p <= totalPages; p += 1) {
          // eslint-disable-next-line no-await-in-loop
          const extra = await admissionCycleService.meritList(cycle._id, {
            classId, page: p, limit,
          });
          allItems.push(...extra.items);
        }
      }
      await generatePdf(
        <MeritListDocument
          apps={allItems}
          cycle={cycle}
          className={className}
          seatSnapshot={data.seatSnapshot}
          branding={branding}
          meta={{
            title: 'Merit List',
            subtitle: [cycle.name, className].filter(Boolean).join(' · '),
            generatedAt: new Date().toISOString(),
          }}
        />,
        `merit-list-${(className || 'class').replace(/\s+/g, '-').toLowerCase()}.pdf`,
      );
    } finally {
      setExporting(false);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────
  if (classOptions.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info" variant="outlined">
          No classes are configured on this cycle. Add classes under <b>Settings → Seat matrix</b>.
        </Alert>
      </Box>
    );
  }

  const seatCap = data?.seatSnapshot?.hardCap;
  const columns = 5 + (plan.test ? 1 : 0) + (plan.interview ? 1 : 0) + (plan.custom ? 1 : 0);
  const ranked = (app: AdmissionApplication) => app.meritRank != null;

  return (
    <Box>
      <TabToolbar
        summary={data ? `${data.pagination.totalItems} on the list` : undefined}
        onRefresh={load}
        refreshing={loading}
        actions={(
          <>
            {onUploadMarks && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<Grading fontSize="small" />}
                onClick={onUploadMarks}
                sx={{ textTransform: 'none' }}
              >
                Upload marks
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<BusyIcon busy={exporting} size={14}><PictureAsPdfOutlined fontSize="small" /></BusyIcon>}
              onClick={downloadPdf}
              disabled={pdfLoading || exporting || !data || data.items.length === 0}
              sx={{ textTransform: 'none' }}
            >
              {exporting ? 'Preparing…' : 'Download PDF'}
            </Button>
          </>
        )}
      >
        <ClassSearchFilters
          classOptions={classOptions}
          classId={classId}
          onClassChange={setClassId}
          search={searchInput}
          onSearchChange={setSearchInput}
          searchPlaceholder="Find applicant on this page…"
        />
      </TabToolbar>

      {/* ── Seat snapshot ───────────────────────────────────────────────── */}
      {data?.seatSnapshot && <SeatSnapshotStrip snapshot={data.seatSnapshot} />}

      {/* Progress on refetch — stays below the toolbar so the toolbar never
          flickers while a new class loads. */}
      {loading && data && (
        <LinearProgress sx={{ height: 2 }} />
      )}

      {/* ── Body ────────────────────────────────────────────────────────── */}
      <Box sx={{ px: 2.5, py: 2.5 }}>
        {!loading && (!data || data.items.length === 0) ? (
          <EmptyState
            icon={<EmojiEvents />}
            title="No ranked applicants yet"
            description={plan.hasAssessment
              ? 'Applicants appear here once they are assessed. Enter their marks on the Assessment tab of the applicant drawer.'
              : 'Applicants appear here once they are under review.'}
            dense
          />
        ) : (
          <>
            <TableCard
              footer={data?.seatSnapshot
                ? `Rows above the cut-line are inside the hard cap of ${data.seatSnapshot.hardCap} seats (${data.seatSnapshot.totalSeats} base + ${data.seatSnapshot.overbookPercent}% overbook).`
                : undefined}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...tableHeadCellSx, width: 64 }}>Rank</TableCell>
                    <TableCell sx={tableHeadCellSx}>Applicant</TableCell>
                    {plan.test && <TableCell align="right" sx={tableHeadCellSx}>Test</TableCell>}
                    {plan.interview && <TableCell align="right" sx={tableHeadCellSx}>{roundsLabel(plan.interview.rounds)}</TableCell>}
                    {plan.custom && <TableCell align="right" sx={tableHeadCellSx}>Additional</TableCell>}
                    <TableCell align="right" sx={tableHeadCellSx}>Prev %</TableCell>
                    <TableCell align="right" sx={tableHeadCellSx}>Merit score</TableCell>
                    <TableCell sx={tableHeadCellSx}>Status</TableCell>
                    <TableCell align="right" sx={{ ...tableHeadCellSx, width: 56 }}>&nbsp;</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && !data ? (
                    <TableRowSkeletons columns={columns + 1} rows={6} />
                  ) : visibleItems.length === 0 ? (
                    <NoSearchMatchRow colSpan={columns + 1} search={search} onClear={() => setSearchInput('')} />
                  ) : (
                    visibleItems.map((app, idx) => {
                      const prev = visibleItems[idx - 1];
                      const underCap = ranked(app) && seatCap != null && app.meritRank! <= seatCap;
                      const prevUnderCap = !!prev && ranked(prev) && seatCap != null && prev.meritRank! <= seatCap;
                      const firstUnranked = !ranked(app) && (!prev || ranked(prev)) && idx > 0;
                      return (
                        <React.Fragment key={app._id}>
                          {prevUnderCap && !underCap && seatCap != null && (
                            <SeatCutLineRow colSpan={columns + 1} seats={seatCap} />
                          )}
                          {firstUnranked && (
                            <TableRow>
                              <TableCell colSpan={columns + 1} sx={{ py: 1, bgcolor: 'action.hover' }}>
                                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                                  Not ranked yet — marks missing
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                          <MeritRow
                            app={app}
                            plan={plan}
                            underCap={underCap}
                            onOpen={() => setOpenId(app._id)}
                            onMenu={(anchor) => setRowMenu({ anchor, app })}
                          />
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableCard>

            {data && (
              <TablePagination
                component="div"
                count={data.pagination.totalItems}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={limit}
                onRowsPerPageChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={PAGE_SIZE_OPTIONS}
              />
            )}
          </>
        )}
      </Box>

      <ApplicationActionsMenu
        app={rowMenu?.app ?? null}
        cycle={cycle}
        anchorEl={rowMenu?.anchor ?? null}
        onClose={() => setRowMenu(null)}
        onMutated={load}
      />

      {openId && (
        <CandidateDrawer
          applicationId={openId}
          cycle={cycle}
          onClose={() => setOpenId(null)}
          onMutated={load}
        />
      )}

      <PdfRenderingDialog
        open={exporting}
        label="Rendering merit list"
        hint={data ? `${data.pagination.totalItems} applicants` : undefined}
      />
    </Box>
  );
};

// ─── Row ────────────────────────────────────────────────────────────────────
interface RowProps {
  app: AdmissionApplication;
  plan: AssessmentPlan;
  underCap: boolean;
  onOpen: () => void;
  onMenu: (anchor: HTMLElement) => void;
}

const MeritRow: React.FC<RowProps> = ({ app, plan, underCap, onOpen, onMenu }) => {
  const outcome = testOutcome(app, plan);
  const interview = useMemo(() => {
    if (!plan.interview) return null;
    const scored = plan.interview.rounds.map(r => (app.interviewRounds || []).find(x => x.roundKey === r.key));
    if (scored.some(s => !s)) return null;
    return { obtained: scored.reduce((t, s) => t + s!.totalMarks, 0), max: plan.interview.rounds.reduce((t, r) => t + r.maxMarks, 0) };
  }, [app.interviewRounds, plan.interview]);
  const custom = useMemo(() => {
    if (!plan.custom) return null;
    const values = plan.custom.criteria.map(c => app.customScores?.[c.key]);
    if (values.some(v => v == null)) return null;
    return { obtained: values.reduce((t: number, v) => t + (v as number), 0), max: plan.custom.criteria.reduce((t, c) => t + c.maxMarks, 0) };
  }, [app.customScores, plan.custom]);
  const awaitingApproval = !!pendingOverride(app);

  const marks = (m: { obtained: number; max: number } | null) => (m ? `${m.obtained}/${m.max}` : '—');

  return (
    <TableRow hover onClick={onOpen} sx={clickableRowSx}>
      <TableCell>
        {app.meritRank != null ? <RankBadge rank={app.meritRank} inside={underCap} /> : <Typography variant="body2" color="text.disabled">—</Typography>}
      </TableCell>
      <TableCell>
        <ApplicantCell app={app} />
        {app.meritRank == null && !!app.meritGaps?.length && (
          <Typography variant="caption" color="warning.dark" sx={{ display: 'block' }}>Missing: {app.meritGaps.join(', ')}</Typography>
        )}
      </TableCell>
      {plan.test && (
        <TableCell align="right" sx={numCellSx}>
          {outcome ? (
            <Box component="span" sx={{ color: outcome.passed ? 'text.primary' : 'error.main', fontWeight: outcome.passed ? 400 : 600 }}>
              {outcome.obtained}/{outcome.max}
            </Box>
          ) : '—'}
        </TableCell>
      )}
      {plan.interview && <TableCell align="right" sx={numCellSx}>{marks(interview)}</TableCell>}
      {plan.custom && <TableCell align="right" sx={numCellSx}>{marks(custom)}</TableCell>}
      <TableCell align="right" sx={numCellSx}>{app.previousPercentage != null ? `${app.previousPercentage}%` : '—'}</TableCell>
      <TableCell align="right" sx={{ ...numCellSx, fontWeight: 700 }}>
        {app.computedMeritScore != null ? fmtNumber(app.computedMeritScore, 2) : '—'}
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          <StatusChip status={{ kind: 'application', value: app.status }} />
          {outcome && !outcome.passed && <ToneChip label="Below pass mark" tone="error" />}
          {awaitingApproval && <ToneChip label="Awaiting approval" tone="warning" />}
        </Box>
      </TableCell>
      <RowActionsCell app={app} onMenu={onMenu} />
    </TableRow>
  );
};

export default MeritListTab;
